import { BrgListItemDto } from '../../types';
import { DateInputDDMMYYYY } from '../shared/DateInputDDMMYYYY';

/**
 * Form values for budget request creation
 */
export interface BudgetRequestFormValues {
  brgId: string;
  submittedBy: string;
  requestAmount: string; // Stored as string for formatting, converted to number on submit
  projectName: string;
  scheduledDate: string;
  purpose: string;
  description: string;
}

/**
 * Form errors for budget request
 */
export interface BudgetRequestFormErrors {
  brgId?: string;
  submittedBy?: string;
  requestAmount?: string;
  purpose?: string;
  description?: string;
}

interface BudgetRequestFormFieldsProps {
  values: BudgetRequestFormValues;
  errors: BudgetRequestFormErrors;
  brgs: BrgListItemDto[];
  onChange: (field: keyof BudgetRequestFormValues, value: string) => void;
  disabled?: boolean;
  isLoadingBrgs?: boolean;
}

const MAX_TEXT_LENGTH = 500;
const MAX_DESCRIPTION_LENGTH = 2000;
const FIELD_SPACING = '24px';
const FIELD_MARGIN_BOTTOM = '20px';
const LABEL_MARGIN_BOTTOM = '8px';
const REQUIRED_ASTERISK = '*';

/**
 * Budget Request Form Fields Component
 *
 * Renders the form fields for creating a new budget request.
 * Includes all required and optional fields with proper validation styling.
 */
export function BudgetRequestFormFields({
  values,
  errors,
  brgs,
  onChange,
  disabled = false,
  isLoadingBrgs = false,
}: BudgetRequestFormFieldsProps) {
  const inputStyles = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
    disabled: {
      backgroundColor: '#f5f5f5',
      color: '#999',
      cursor: 'not-allowed',
    },
  } as const;

  const errorInputStyles = {
    borderColor: '#d32f2f',
  };

  const labelStyles = {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#333',
    marginBottom: LABEL_MARGIN_BOTTOM,
    display: 'block',
  };

  const errorStyles = {
    fontSize: '0.85rem',
    color: '#d32f2f',
    marginTop: '6px',
    display: 'block',
  };

  const sectionHeaderStyles = {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: '#666',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: FIELD_SPACING,
    marginTop: '0',
  };

  const asteriskStyles = {
    color: '#d32f2f',
    marginLeft: '4px',
  };

  // Helper to get input styles based on error state
  const getInputStyle = (hasError: boolean) => ({
    ...inputStyles,
    ...(hasError ? errorInputStyles : {}),
    ...(disabled ? inputStyles.disabled : {}),
  });

  return (
    <>
      {/* BRG INFORMATION SECTION */}
      <h3 style={sectionHeaderStyles}>BRG INFORMATION</h3>

      {/* BRG Name Field */}
      <div style={{ marginBottom: FIELD_MARGIN_BOTTOM }}>
        <label htmlFor="brgId" style={labelStyles}>
          BRG Name
          <span style={asteriskStyles}>{REQUIRED_ASTERISK}</span>
        </label>
        <select
          id="brgId"
          value={values.brgId}
          onChange={(e) => onChange('brgId', e.target.value)}
          disabled={disabled || isLoadingBrgs}
          style={getInputStyle(!!errors.brgId)}
          required
        >
          <option value="">Select your BRG</option>
          {brgs.map((brg) => (
            <option key={brg.brgId} value={String(brg.brgId)}>
              {brg.name}
            </option>
          ))}
        </select>
        {errors.brgId && <span style={errorStyles}>{errors.brgId}</span>}
      </div>

      {/* Submitted By Field */}
      <div style={{ marginBottom: FIELD_MARGIN_BOTTOM }}>
        <label htmlFor="submittedBy" style={labelStyles}>
          Submitted By
          <span style={asteriskStyles}>{REQUIRED_ASTERISK}</span>
        </label>
        <input
          id="submittedBy"
          type="text"
          value={values.submittedBy}
          onChange={(e) => onChange('submittedBy', e.target.value)}
          disabled={true} // Always disabled - auto-populated from current user
          style={{
            ...getInputStyle(!!errors.submittedBy),
            backgroundColor: '#f5f5f5',
          }}
          placeholder="Auto-populated from your profile"
          required
        />
        <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '4px', margin: '4px 0 0 0' }}>
          Auto-populated from your profile
        </p>
        {errors.submittedBy && <span style={errorStyles}>{errors.submittedBy}</span>}
      </div>

      {/* REQUEST DETAILS SECTION */}
      <h3 style={{ ...sectionHeaderStyles, marginTop: '32px' }}>REQUEST DETAILS</h3>

      {/* Request Amount Field */}
      <div style={{ marginBottom: FIELD_MARGIN_BOTTOM }}>
        <label htmlFor="requestAmount" style={labelStyles}>
          Request Amount
          <span style={asteriskStyles}>{REQUIRED_ASTERISK}</span>
        </label>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <span
            style={{
              position: 'absolute',
              left: '12px',
              color: '#666',
              fontSize: '0.95rem',
              pointerEvents: 'none',
            }}
          >
            $
          </span>
          <input
            id="requestAmount"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={values.requestAmount}
            onChange={(e) => onChange('requestAmount', e.target.value)}
            onWheel={(e) => e.currentTarget.blur()}
            disabled={disabled}
            style={{
              ...getInputStyle(!!errors.requestAmount),
              paddingLeft: '32px', // Make room for $ symbol
            }}
            placeholder="0.00"
            required
          />
        </div>
        {errors.requestAmount && <span style={errorStyles}>{errors.requestAmount}</span>}
      </div>

      {/* Project / Event Field (Optional) */}
      <div style={{ marginBottom: FIELD_MARGIN_BOTTOM }}>
        <label htmlFor="projectName" style={labelStyles}>
          Project / Event
          <span style={{ color: '#999', marginLeft: '4px', fontSize: '0.85rem' }}>(optional)</span>
        </label>
        <input
          id="projectName"
          type="text"
          value={values.projectName}
          onChange={(e) => onChange('projectName', e.target.value.slice(0, MAX_TEXT_LENGTH))}
          disabled={disabled}
          style={getInputStyle(false)}
          placeholder="e.g. Annual Kickoff Event"
          maxLength={MAX_TEXT_LENGTH}
        />
        <p
          style={{
            fontSize: '0.8rem',
            color: '#999',
            margin: '4px 0 0 0',
            textAlign: 'right',
          }}
        >
          {values.projectName.length}/{MAX_TEXT_LENGTH}
        </p>
      </div>

      {/* Scheduled Date Field (Optional) */}
      <div style={{ marginBottom: FIELD_MARGIN_BOTTOM }}>
        <label htmlFor="scheduledDate" style={labelStyles}>
          Scheduled Date
          <span style={{ color: '#999', marginLeft: '4px', fontSize: '0.85rem' }}>(optional)</span>
        </label>
        <DateInputDDMMYYYY
          value={values.scheduledDate}
          onChange={(val) => onChange('scheduledDate', val)}
          disabled={disabled}
          style={getInputStyle(false)}
        />
      </div>

      {/* Purpose / Description Field */}
      <div style={{ marginBottom: FIELD_MARGIN_BOTTOM }}>
        <label htmlFor="purpose" style={labelStyles}>
          Purpose / Description
          <span style={asteriskStyles}>{REQUIRED_ASTERISK}</span>
        </label>
        <input
          id="purpose"
          type="text"
          value={values.purpose}
          onChange={(e) => onChange('purpose', e.target.value.slice(0, MAX_TEXT_LENGTH))}
          disabled={disabled}
          style={getInputStyle(!!errors.purpose)}
          placeholder="Brief purpose of this request"
          maxLength={MAX_TEXT_LENGTH}
          required
        />
        <p
          style={{
            fontSize: '0.8rem',
            color: '#999',
            margin: '4px 0 0 0',
            textAlign: 'right',
          }}
        >
          {values.purpose.length}/{MAX_TEXT_LENGTH}
        </p>
        {errors.purpose && <span style={errorStyles}>{errors.purpose}</span>}
      </div>

      {/* Request Description Field */}
      <div style={{ marginBottom: FIELD_MARGIN_BOTTOM }}>
        <label htmlFor="description" style={labelStyles}>
          Request Description
          <span style={asteriskStyles}>{REQUIRED_ASTERISK}</span>
        </label>
        <textarea
          id="description"
          value={values.description}
          onChange={(e) => onChange('description', e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
          disabled={disabled}
          style={{
            ...getInputStyle(!!errors.description),
            minHeight: '120px',
            resize: 'vertical',
            fontFamily: 'inherit',
            paddingTop: '10px',
          }}
          placeholder="Provide a detailed description of how the funds will be used..."
          maxLength={MAX_DESCRIPTION_LENGTH}
          required
        />
        <p
          style={{
            fontSize: '0.8rem',
            color: '#999',
            margin: '4px 0 0 0',
            textAlign: 'right',
          }}
        >
          {values.description.length}/{MAX_DESCRIPTION_LENGTH}
        </p>
        {errors.description && <span style={errorStyles}>{errors.description}</span>}
      </div>
    </>
  );
}
